/**
 * External Imports
 */
import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Internal Imports
 */
import EmployeeProfileHeader from '../../components/profileHeaders/employeeProfileHeader';
import Round from '../../components/round';
import Loading from '../../components/shared/Loading';
import HistorySideNav from '../../components/sideNav/historySideNav';
import HistorySideDrawer from '../../components/sideNav/historySideDrawer';

import { RootState } from '../../redux/store';
import {
  getTasksByEmployeeId,
  clearEmployeeRoundHistory
} from '../../redux/actions';
import {
  useGetEmployeeNotesQuery,
  useGetEmployeeToRoundQuery
} from '../../redux/apis/Employees.api';

/**
 * Global Type Definition Imports
 */
import { Feedback, Issue } from '../../config/interfaces';

/**
 * Style Imports
 */
import { ProfileContainer } from './styles/Employee.styles';
import { useGetEmployeeRoundHistoryQuery } from '../../redux/apis/Reports.api';

interface EmployeeProfileProps {
  issues?: Issue[];
  feedback?: Feedback[];
}

const EmployeeProfile: FC<EmployeeProfileProps> = ({ issues, feedback }) => {
  const dispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState<string>('');

  const { state } = useLocation();
  const navigate = useNavigate();
  const { employeeId = '' } =
    (state as {
      employeeId: string;
    }) || {};

  const {
    data: employeeToRoundData,
    // isError: isGetEmployeeToRoundError,
    isFetching: isGetEmployeeToRoundFetching
    // isLoading: isGetEmployeeToRoundLoading
  } = useGetEmployeeToRoundQuery(employeeId, {
    refetchOnMountOrArgChange: true,
    skip: !employeeId
  });

  const {
    data: employeeNotesData,
    // isError: isGetEmployeeNotesError,
    // isFetching: isGetEmployeeNotesFetching,
    isLoading: isGetEmployeeNotesLoading
  } = useGetEmployeeNotesQuery(employeeToRoundData?.employeeId, {
    pollingInterval: 60000,
    refetchOnMountOrArgChange: true,
    skip: !employeeToRoundData?.employeeId
  });
  const {
    data: employeeRoundHistoryData,
    isLoading: isGetEmployeeRoundHistoryLoading
  } = useGetEmployeeRoundHistoryQuery(
    {
      employeeId,
      roundingTypes: ['employee', 'cned', 'csrn']
    },
    {
      skip: !employeeId,
      refetchOnMountOrArgChange: true
    }
  );

  useLayoutEffect(() => {
    // Perform layout effect logic here
    if (employeeToRoundData) {
      dispatch(
        getTasksByEmployeeId({
          employeeId: employeeToRoundData?.hcaid,
          roundingTypes: ['employee', 'cned', 'csrn']
        })
      );
    } else if (!employeeId) {
      // TODO: Hack to return to patient census when refreshing or navigating straight to profile
      navigate('/employees/list/user');
    }
  }, [employeeToRoundData]);

  useEffect(() => {
    return () => {
      dispatch(clearEmployeeRoundHistory());
    };
  }, []);

  return (
    <ProfileContainer>
      {isGetEmployeeToRoundFetching ||
      isGetEmployeeNotesLoading ||
      isGetEmployeeRoundHistoryLoading ? (
        <Loading centered label="employee data..." />
      ) : (
        <>
          <section>
            <EmployeeProfileHeader selectedEmployee={employeeToRoundData} />
          </section>
          <section className="d-flex" style={{ height: 'calc(100vh - 163px)' }}>
            <HistorySideNav
              selectedEmployee={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              type="employee"
            />
            <HistorySideDrawer
              selectedTab={selectedTab}
              selectedPerson={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
              roundHistory={employeeRoundHistoryData}
              tasks={[...(issues || []), ...(feedback || [])]}
              type="employee"
              setSelectedTab={setSelectedTab}
            />
            <Round type="employee" selectedEmployee={employeeToRoundData} />
          </section>
        </>
      )}
    </ProfileContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    feedback: state.TaskReducer.employeeFeedback,
    issues: state.TaskReducer.employeeIssues
  };
};

export default connect(mapReduxStateToProps)(EmployeeProfile);
